import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Button,
  Box,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import {
  uploadPersonnelDocument,
  fetchPersonnelDocuments,
  deletePersonnelDocument,
  downloadPersonnelDocument,
} from "../../services/personnel/personnelDocument.service";
import { getToken } from "services/auth.service";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import FileDropzone from "../../../contracts/components/projects/FileDropzone";
import { fetchListTitleNames } from "../../services/personnel/listTitleName.service";
import { fetchListQualificationNames } from "../../services/personnel/listQualificationName.service";
import { fetchListMajorNames } from "../../services/personnel/listMajorName.service";
import {
  fetchPersonnel,
  createPersonnel,
  updatePersonnel,
  getPersonnelById,
} from "../../services/personnel/personnel.service";
import api from "services/api";
import { fetchListCertificationNames } from "../../services/personnel/listCertificationName.service";
import { fetchDocumentTypes, getDocumentTypes } from "../../services/documentType.service";
import departmentService from "../../services/personnel/department.service";
import enterpriseService from "../../services/personnel/enterprise.service";
import qualificationService from "../../services/personnel/qualification.service";
import certificateService from "../../services/personnel/certificate.service";
import { openDocumentPreviewInNewTab } from "utils/documentPreview";
import { formatDate } from "utils/formatters";
import { AppButton } from "components/common/buttons";
import { AppTab, AppTabPanel } from "components/common/tabs";
import App from "App";

const AddPersonnelModal = ({
  open,
  onClose,
  onPersonnelAdded,
  personnelId,
  isEdit = false,
  initialTabIndex = 0,
}) => {
  const [tab, setTab] = useState(0);
  // Tab 1: Thông tin nhân sự
  const [personnelForm, setPersonnelForm] = useState({
    fullName: "",
    title: "",
    email: "",
    phoneNumber: "",
    enterpriseId: "",
    departmentId: "",
    displayOrder: 0,
    isActive: true,
  });
  const [localPersonnelId, setLocalPersonnelId] = useState(personnelId);
  const [personnelError, setPersonnelError] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [savedDocuments, setSavedDocuments] = useState([]);
  // State quản lý thông báo
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    message: "",
    severity: "info", // success, error, warning, info
  });

  // Tab 2: Trình độ
  const [qualifications, setQualifications] = useState([]);
  const [qualificationForm, setQualificationForm] = useState({
    qualificationName: "",
    major: "",
    graduationYear: "",
  });
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [majorOptions, setMajorOptions] = useState([]);

  // Tab 3: Chứng chỉ
  const [certificates, setCertificates] = useState([]);
  const [certificateForm, setCertificateForm] = useState({
    certificateName: "",
    issueDate: "",
  });
  const [certificationOptions, setCertificationOptions] = useState([]);

  // Tab 4: Đính kèm
  const [docUpload, setDocUpload] = useState({
    file: null,
    documentType: "",
    description: "",
  });
  const [documents, setDocuments] = useState([]);
  const [docTypes, setDocTypes] = useState([
    { value: "CMND/CCCD", label: "CMND/CCCD" },
    { value: "Bằng cấp", label: "Bằng cấp" },
    { value: "Hợp đồng lao động", label: "Hợp đồng lao động" },
    { value: "Khác", label: "Khác" },
  ]);
  const [titleOptions, setTitleOptions] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [enterpriseOptions, setEnterpriseOptions] = useState([]);
  const [filteredDepartmentOptions, setFilteredDepartmentOptions] = useState(
    [],
  );

  // State cho preview file
  const [previewModal, setPreviewModal] = useState({
    open: false,
    file: null,
    fileName: "",
    fileType: "",
    fileBlob: null,
  });

  const tabs = [
    { label: "Thông tin nhân sự", value: 0 },
    { label: "Trình độ", value: 1 },
    { label: "Chứng chỉ", value: 2 },
    { label: "Đính kèm", value: 3 },
  ];

  // Function để tự động lấy số thứ tự tiếp theo
  const handleAutoDisplayOrder = async () => {
    try {
      // Gọi API để lấy tất cả nhân sự
      const result = await fetchPersonnel(1000, 1, "", "displayOrder", "desc");

      if (result && result.items && result.items.length > 0) {
        // Tìm số thứ tự cao nhất
        const maxDisplayOrder = Math.max(
          ...result.items.map((item) => item.displayOrder || 0),
        );

        // Cập nhật form với số thứ tự cao nhất + 1
        setPersonnelForm((prev) => ({
          ...prev,
          displayOrder: maxDisplayOrder + 1,
        }));
      } else {
        // Nếu chưa có nhân sự nào, bắt đầu từ 1
        setPersonnelForm((prev) => ({
          ...prev,
          displayOrder: 1,
        }));
      }
    } catch (error) {
      console.error("Error getting max display order:", error);
      // Nếu có lỗi, đặt về 1
      setPersonnelForm((prev) => ({
        ...prev,
        displayOrder: 1,
      }));
    }
  };

  // Nhận tab ban đầu từ props và đảm bảo load dữ liệu phù hợp
  useEffect(() => {
    if (open) {
      setTab(initialTabIndex > 0 ? initialTabIndex : 0);
    }
  }, [initialTabIndex, open]);

  // Tab 4: Đính kèm
  const loadDocuments = useCallback(async () => {
    // Kiểm tra ID và đảm bảo lấy ID mới nhất
    if (!localPersonnelId && !personnelId) {
      // Không có ID nào, xóa dữ liệu tài liệu cũ
      setSavedDocuments([]);
      return;
    }

    // Lưu ID vào biến local để tránh race condition
    const id = localPersonnelId || personnelId;

    // Tạo biến để theo dõi ID hiện tại
    const currentLoadId = id;

    try {
      // Thiết lập state đang tải
      setSavedDocuments([]);

      // Gọi API
      const res = await fetchPersonnelDocuments(id);

      // Kiểm tra ID hiện tại xem có còn khớp với khi bắt đầu gọi API không
      const currentId = localPersonnelId || personnelId;
      if (currentLoadId !== currentId) {
        return;
      }

      if (res && res.data) {
        let documents = [];

        // Xử lý cấu trúc dữ liệu trả về
        if (Array.isArray(res.data)) {
          documents = res.data;
        } else if (res.data.data && Array.isArray(res.data.data)) {
          documents = res.data.data;
        }

        setSavedDocuments(documents);
      } else {
        setSavedDocuments([]);
      }
    } catch (error) {
      setSavedDocuments([]);
    }
  }, [localPersonnelId, personnelId]);

  const resetForm = () => {
    // Reset main form
    setPersonnelForm({
      fullName: "",
      title: "",
      email: "",
      phoneNumber: "",
      enterpriseId: "",
      departmentId: "",
      displayOrder: 0,
      isActive: true,
    });

    // Reset all tabs data
    setQualifications([]);
    setCertificates([]);
    setDocuments([]);
    setDocUpload({ file: null, documentType: "", description: "" });

    // Reset errors and other state
    setPersonnelError({});
    setSavedDocuments([]);
    setLocalPersonnelId(null); // Đặt lại localPersonnelId để tránh lấy dữ liệu nhân sự cũ

    // Reset to first tab
    setTab(0);

    // Reset filtered departments
    setFilteredDepartmentOptions([]);
  };
  const handleTabChange = async (e, newValue) => {
    setTab(newValue);

    // Khi chuyển đến tab Đính kèm (tab 3), làm mới danh sách tài liệu
    if (newValue === 3 && (localPersonnelId || personnelId)) {
      try {
        // Xóa danh sách tài liệu cũ trước khi tải mới
        setSavedDocuments([]);
        await loadDocuments();
      } catch (error) {
        // Ignore document refresh errors
      }
    }
  };

  // Tab 1: Xử lý nhập liệu và tạo nhân sự
  const handlePersonnelChange = (e) => {
    const updatedForm = { ...personnelForm, [e.target.name]: e.target.value };
    setPersonnelForm(updatedForm);
    setPersonnelError(validatePersonnelForm(updatedForm));
  };

  const validatePersonnelForm = (form) => {
    const errors = {};
    if (!form.fullName || form.fullName.trim() === "") {
      errors.fullName = "Họ và tên là bắt buộc";
    }
    if (!form.title || form.title.trim() === "") {
      errors.title = "Chức vụ là bắt buộc";
    }
    if (form.email && form.email.trim() !== "") {
      // Simple email regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        errors.email = "Email không đúng định dạng";
      }
    }
    return errors;
  };

  // Tab 2: Thêm trình độ (chỉ thêm vào danh sách, không gọi API)
  const handleAddQualification = () => {
    if (
      !qualificationForm.qualificationName ||
      !qualificationForm.major ||
      !qualificationForm.graduationYear
    )
      return;
    setQualifications([...qualifications, { ...qualificationForm }]);
    setQualificationForm({
      qualificationName: "",
      major: "",
      graduationYear: "",
    });
  };
  const handleDeleteQualification = (idx) => {
    setQualifications(qualifications.filter((_, i) => i !== idx));
  };

  // Tab 3: Thêm chứng chỉ (chỉ thêm vào danh sách, không gọi API)
  const handleAddCertificate = () => {
    if (!certificateForm.certificateName || !certificateForm.issueDate) return;
    setCertificates([...certificates, { ...certificateForm }]);
    setCertificateForm({ certificateName: "", issueDate: "" });
  };
  const handleDeleteCertificate = (idx) => {
    setCertificates(certificates.filter((_, i) => i !== idx));
  };
  // Hàm này không còn được sử dụng vì đã thay thế bằng FileDropzone
  // const handleDocFileChange = (e) => {
  //   setDocUpload({ ...docUpload, file: e.target.files[0] });
  // };
  const handleDocTypeChange = (e) => {
    setDocUpload({ ...docUpload, documentType: e.target.value });
  };
  const handleDocDescChange = (e) => {
    setDocUpload({ ...docUpload, description: e.target.value });
  };
  const handleAddDocument = () => {
    if (!docUpload.file || !docUpload.documentType) return;
    setDocuments([...documents, { ...docUpload }]);
    setDocUpload({ file: null, documentType: "", description: "" });
  };
  const handleDeleteDocument = (idx) => {
    setDocuments(documents.filter((_, i) => i !== idx));
  };

  // Function để xem trước file
  const handlePreviewDocument = async (doc, isSavedDoc = false) => {
    const previewData = await openDocumentPreviewInNewTab({
      file: doc,
      isRemote: isSavedDoc,
      downloadFn: downloadPersonnelDocument,
      onAlert: setAlertInfo,
    });
  };

  // Handle enterprise change to filter departments
  useEffect(() => {
    // Kiểm tra có enterpriseId không
    if (personnelForm.enterpriseId) {
      const enterpriseIdNum = parseInt(personnelForm.enterpriseId);

      if (departmentOptions && departmentOptions.length > 0) {
        // Lọc các phòng ban thuộc xí nghiệp đã chọn
        // Chuyển đổi cả hai bên sang số trước khi so sánh để tránh lỗi so sánh kiểu dữ liệu
        const filtered = departmentOptions.filter((dept) => {
          // Chuyển enterpriseId của dept sang dạng số
          const deptEnterpriseId =
            typeof dept.enterpriseId === "string"
              ? parseInt(dept.enterpriseId)
              : dept.enterpriseId;

          return deptEnterpriseId === enterpriseIdNum;
        });

        setFilteredDepartmentOptions(filtered);

        // Kiểm tra xem phòng ban hiện tại có thuộc danh sách lọc không
        if (
          personnelForm.departmentId &&
          !filtered.some((dept) => {
            // Chuyển cả hai giá trị sang chuỗi để so sánh nhất quán
            return (
              String(dept.departmentId) === String(personnelForm.departmentId)
            );
          })
        ) {
          setPersonnelForm((prev) => ({
            ...prev,
            departmentId: "",
          }));
        }
      } else {
        setFilteredDepartmentOptions([]);
      }
    } else {
      // Nếu không chọn xí nghiệp thì không lọc phòng ban
      setFilteredDepartmentOptions([]);

      // Xóa phòng ban đã chọn khi không có xí nghiệp
      if (personnelForm.departmentId) {
        setPersonnelForm((prev) => ({ ...prev, departmentId: "" }));
      }
    }
  }, [
    personnelForm.enterpriseId,
    personnelForm.departmentId,
    departmentOptions,
  ]);

  // Theo dõi thay đổi của ID và tab hiện tại để tải lại tài liệu
  useEffect(() => {
    // Chỉ tải tài liệu khi cần thiết: khi mở dialog ở chế độ chỉnh sửa và đang ở tab Đính kèm
    if (isEdit && open && (localPersonnelId || personnelId) && tab === 3) {
      loadDocuments();
    }
  }, [isEdit, open, localPersonnelId, personnelId, loadDocuments, tab]);

  // Lưu toàn bộ khi nhấn Lưu nhân sự
  const handleSavePersonnel = async () => {
    setSubmitting(true);
    const errors = validatePersonnelForm(personnelForm);
    setPersonnelError(errors);
    if (Object.keys(errors).length > 0) {
      setSubmitting(false);
      return null;
    }
    try {
      let currentPersonnelId;

      if (isEdit && personnelId) {
        // Cập nhật nhân sự hiện có
        await updatePersonnel(personnelId, personnelForm);
        currentPersonnelId = personnelId;
      } else {
        // Tạo nhân sự mới
        const personnelRes = await createPersonnel(personnelForm);
        currentPersonnelId = Number(personnelRes.data.data.personnelId);
        setLocalPersonnelId(currentPersonnelId);
      }

      // Xử lý trình độ với error handling tốt hơn
      if (isEdit) {
        try {
          // Luôn xóa tất cả trình độ cũ khi là chế độ edit
          // Sau đó sẽ thêm lại những trình độ mới (nếu có)
          await qualificationService.deleteQualificationsByPersonnelId(
            currentPersonnelId,
          );
        } catch (error) {
          // Bỏ qua lỗi xóa - có thể đã không có data để xóa
          console.warn("Không thể xóa trình độ cũ:", error);
        }
      }

      // Thêm trình độ mới với error handling từng item
      for (const q of qualifications) {
        try {
          await qualificationService.createQualification({
            personnelId: currentPersonnelId,
            qualificationName: q.qualificationName,
            major: q.major,
            graduationYear: q.graduationYear
              ? `${q.graduationYear}-01-01T00:00:00Z`
              : null,
          });
        } catch (error) {
          // Tiếp tục với qualification tiếp theo nếu có lỗi
        }
      }

      // Xử lý chứng chỉ với error handling tốt hơn
      if (isEdit) {
        try {
          // Luôn xóa tất cả chứng chỉ cũ khi là chế độ edit
          await certificateService.deleteCertificatesByPersonnelId(
            currentPersonnelId,
          );
        } catch (error) {
          // Bỏ qua lỗi xóa - có thể đã không có data để xóa
        }
      }

      // Thêm chứng chỉ mới với error handling từng item
      for (const c of certificates) {
        try {
          await certificateService.createCertificate({
            personnelId: currentPersonnelId,
            certificateName: c.certificateName,
            issueDate: c.issueDate ? new Date(c.issueDate).toISOString() : null,
          });
        } catch (error) {
          // Tiếp tục với certificate tiếp theo nếu có lỗi
        }
      }

      // Upload file đính kèm mới (nếu có)
      for (const doc of documents) {
        await uploadPersonnelDocument({
          personnelId: String(currentPersonnelId),
          documentType: doc.documentType || undefined,
          description: doc.description || undefined,
          file: doc.file,
        });
      }

      // Đợi một chút để đảm bảo dữ liệu được lưu hoàn toàn
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (onPersonnelAdded) onPersonnelAdded();

      // Reset form nếu là thêm mới
      if (!isEdit) {
        resetForm();
      }

      // Đóng dialog
      onClose && onClose();
      return currentPersonnelId;
    } catch (err) {
      setPersonnelError({
        submit:
          err?.response?.data?.message ||
          (isEdit ? "Cập nhật nhân sự thất bại" : "Tạo nhân sự thất bại"),
      });
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  // Tab 2: Xử lý nhập liệu trình độ
  const handleQualificationChange = (e) => {
    setQualificationForm({
      ...qualificationForm,
      [e.target.name]: e.target.value,
    });
  };

  // Tab 3: Xử lý nhập liệu chứng chỉ
  const handleCertificateChange = (e) => {
    setCertificateForm({
      ...certificateForm,
      [e.target.name]: e.target.value,
    });
  };

  // Xử lý khi dialog được đóng
  const handleClose = () => {
    // Reset lại tab về tab đầu tiên và xóa dữ liệu
    resetForm();
    onClose();
  };

  // Reset dữ liệu khi đóng modal
  useEffect(() => {
    if (!open) {
      // Khi modal đóng, chúng ta reset lại tab và xóa dữ liệu
      setTab(0);
      resetForm();
      // Xóa danh sách tài liệu để tránh hiện dữ liệu cũ
      setSavedDocuments([]);
      // Reset ID nhân sự để tránh dùng ID cũ
      setLocalPersonnelId(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      // Tải tất cả dữ liệu cần thiết
      const loadAllData = async () => {
        try {
          // Tải song song các dữ liệu dropdown
          const [
            titleData,
            departmentRes,
            enterpriseRes,
            qualificationData,
            majorData,
            certificationData,
            documentTypesRes,
          ] = await Promise.all([
            fetchListTitleNames(),
            departmentService.getAll(),
            enterpriseService.getAll(),
            fetchListQualificationNames(),
            fetchListMajorNames(),
            fetchListCertificationNames(),
            // fetchDocumentTypes(),
            getDocumentTypes({
              documentCategory: "Nhân sự",
              pageSize: 1000,
              pageNumber: 1,
              sortColumn: "DocumentCategory",
              sortDirection: "ASC",
            })
          ]);

          // Cập nhật các state với dữ liệu đã tải
          setTitleOptions(Array.isArray(titleData) ? titleData : []);

          // Lưu trữ toàn bộ danh sách phòng ban
          const departments = Array.isArray(departmentRes?.data)
            ? departmentRes.data
            : [];
          setDepartmentOptions(departments);

          // Lưu trữ toàn bộ danh sách xí nghiệp
          const enterprises = Array.isArray(enterpriseRes?.data)
            ? enterpriseRes.data
            : [];
          setEnterpriseOptions(enterprises);

          setQualificationOptions(
            Array.isArray(qualificationData) ? qualificationData : [],
          );
          setMajorOptions(Array.isArray(majorData) ? majorData : []);
          setCertificationOptions(
            Array.isArray(certificationData) ? certificationData : [],
          );
          setDocumentTypeOptions(
            Array.isArray(documentTypesRes?.data) ? documentTypesRes.data : [],
          );

          // Nếu là chế độ chỉnh sửa, tải thông tin nhân sự
          if (isEdit && personnelId) {
            // Inline load personnel data để tránh dependency loop
            try {
              setLocalPersonnelId(personnelId);

              // Tải thông tin nhân sự
              const personnelResponse = await getPersonnelById(personnelId);
              if (personnelResponse?.data?.data) {
                const personnel = personnelResponse.data.data;

                // Xử lý dữ liệu departmentId
                const departmentIdStr =
                  personnel.departmentId !== null &&
                    personnel.departmentId !== undefined
                    ? String(personnel.departmentId)
                    : "";

                // Tìm enterpriseId từ departmentId thông qua departments đã load
                let enterpriseIdStr = "";
                if (departmentIdStr && departments.length > 0) {
                  const department = departments.find(
                    (dept) => String(dept.departmentId) === departmentIdStr,
                  );

                  if (department && department.enterpriseId !== undefined) {
                    enterpriseIdStr = String(department.enterpriseId);
                  }
                }

                // Đặt form data
                setPersonnelForm({
                  fullName: personnel.fullName || "",
                  title: personnel.title || "",
                  email: personnel.email || "",
                  phoneNumber: personnel.phoneNumber || "",
                  enterpriseId: enterpriseIdStr,
                  departmentId: departmentIdStr,
                  displayOrder: personnel.displayOrder || 0,
                  isActive:
                    personnel.isActive !== undefined
                      ? personnel.isActive
                      : true,
                });

                // Cập nhật filteredDepartmentOptions nếu có enterpriseId
                if (enterpriseIdStr && departments.length > 0) {
                  const enterpriseIdNum = parseInt(enterpriseIdStr);
                  const filtered = departments.filter((dept) => {
                    const deptEnterpriseId =
                      typeof dept.enterpriseId === "string"
                        ? parseInt(dept.enterpriseId)
                        : dept.enterpriseId;
                    return deptEnterpriseId === enterpriseIdNum;
                  });
                  setFilteredDepartmentOptions(filtered);
                } else {
                  setFilteredDepartmentOptions([]);
                }

                // Tải danh sách trình độ
                try {
                  const qualificationsResponse =
                    await qualificationService.getQualificationsByPersonnelId(
                      personnelId,
                    );
                  if (qualificationsResponse?.data) {
                    const qualData = qualificationsResponse.data.map((q) => ({
                      qualificationName: q.qualificationName || "",
                      major: q.major || "",
                      graduationYear: q.graduationYear
                        ? new Date(q.graduationYear).getFullYear().toString()
                        : "",
                      id: q.qualificationId,
                    }));
                    setQualifications(qualData);
                  }
                } catch (error) {
                  setQualifications([]);
                }

                // Tải danh sách chứng chỉ
                try {
                  const certificatesResponse =
                    await certificateService.getCertificatesByPersonnelId(
                      personnelId,
                    );
                  if (certificatesResponse?.data) {
                    const certData = certificatesResponse.data.map((c) => ({
                      certificateName: c.certificateName || "",
                      issueDate: c.issueDate
                        ? new Date(c.issueDate).toISOString().split("T")[0]
                        : "",
                      id: c.certificateId,
                    }));
                    setCertificates(certData);
                  }
                } catch (error) {
                  setCertificates([]);
                }

                // Tải danh sách tài liệu đính kèm
                try {
                  // Inline load documents để tránh dependency
                  if (personnelId) {
                    const res = await fetchPersonnelDocuments(personnelId);
                    if (res && res.data) {
                      let documents = [];
                      if (Array.isArray(res.data)) {
                        documents = res.data;
                      } else if (
                        res.data.data &&
                        Array.isArray(res.data.data)
                      ) {
                        documents = res.data.data;
                      }
                      setSavedDocuments(documents);
                    } else {
                      setSavedDocuments([]);
                    }
                  }
                } catch (error) {
                  setSavedDocuments([]);
                }
              }
            } catch (error) {
              setPersonnelError({
                submit:
                  "Không thể tải thông tin nhân sự. Vui lòng thử lại sau.",
              });
            }
          } else {
            // Reset form cho trường hợp thêm mới
            resetForm();
          }
        } catch (error) {
          // Chỉ giữ lại error logging khi thực sự cần thiết cho debugging
        }
      };

      loadAllData();
    }
  }, [open, personnelId, isEdit]); // Loại bỏ loadPersonnelData để tránh vòng lặp

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onExited: () => {
          // Xử lý khi dialog đã đóng hoàn toàn
          setTab(0);
          resetForm();
        },
      }}
    >
      {/* Snackbar hiển thị thông báo */}
      <Snackbar
        open={alertInfo.open}
        autoHideDuration={6000}
        onClose={() => setAlertInfo({ ...alertInfo, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertInfo({ ...alertInfo, open: false })}
          severity={alertInfo.severity}
          sx={{ width: "100%" }}
        >
          {alertInfo.message}
        </Alert>
      </Snackbar>

      <DialogTitle>
        {isEdit ? "Chỉnh sửa nhân sự" : "Thêm mới nhân sự"}
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: "78vh",
          maxHeight: "78vh",
          overflowY: "auto",
        }}
      >
        {/* <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            mb: 2,
            "& .MuiTab-root": {
              fontFamily: "Inter, sans-serif",
            },
          }}
        >
          <Tab label="Thông tin nhân sự" />
          <Tab label="Trình độ" />
          <Tab label="Chứng chỉ" />
          <Tab label="Đính kèm" />
        </Tabs> */}
        <AppTab
          value={tab}
          onChange={handleTabChange}
          tabs={tabs}
        />


        <AppTabPanel value={tab} index={0}>
          {/* Tab 1: Thông tin nhân sự */}
          {tab === 0 && (
            <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
              <TextField
                label="Họ và tên"
                name="fullName"
                value={personnelForm.fullName}
                onChange={handlePersonnelChange}
                required
                error={!!personnelError.fullName}
                helperText={personnelError.fullName}
              />
              <FormControl fullWidth error={!!personnelError.title}>
                <InputLabel>Chức vụ</InputLabel>
                <Select
                  name="title"
                  value={personnelForm.title}
                  label="Chức vụ"
                  onChange={handlePersonnelChange}
                >
                  {titleOptions.map((option) => (
                    <MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {personnelError.title && (
                  <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                    {personnelError.title}
                  </Typography>
                )}
              </FormControl>
              <TextField
                label="Email"
                name="email"
                value={personnelForm.email}
                onChange={handlePersonnelChange}
                error={!!personnelError.email}
                helperText={personnelError.email}
              />
              <TextField
                label="Số điện thoại"
                name="phoneNumber"
                value={personnelForm.phoneNumber}
                onChange={handlePersonnelChange}
              />
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  label="Thứ tự hiển thị"
                  name="displayOrder"
                  type="number"
                  value={personnelForm.displayOrder}
                  onChange={handlePersonnelChange}
                  inputProps={{ min: 0 }}
                  helperText="Thứ tự sắp xếp trong danh sách (0 = đầu tiên)"
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Tự động lấy số thứ tự tiếp theo">
                  <AppButton
                    variant="outlined"
                    // size="medium"
                    onClick={handleAutoDisplayOrder}
                    startIcon={<AutoFixHighIcon />}
                    sx={{
                      // minWidth: "auto",
                      // px: 1,
                      // textTransform: "none",
                      height: "56px",
                      // mt: 0,
                    }}
                  >
                    Auto
                  </AppButton>
                </Tooltip>
              </Box>
              <FormControl fullWidth>
                <InputLabel>Xí nghiệp</InputLabel>
                <Select
                  name="enterpriseId"
                  value={personnelForm.enterpriseId || ""}
                  label="Xí nghiệp"
                  onChange={(e) => {
                    handlePersonnelChange(e);
                  }}
                >
                  <MenuItem value="">Không chọn xí nghiệp</MenuItem>
                  {enterpriseOptions && enterpriseOptions.length > 0 ? (
                    enterpriseOptions.map((enterprise) => (
                      <MenuItem
                        key={enterprise.enterpriseId}
                        value={String(enterprise.enterpriseId)}
                      >
                        {enterprise.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>Đang tải dữ liệu...</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Phòng ban</InputLabel>
                <Select
                  name="departmentId"
                  value={personnelForm.departmentId || ""}
                  label="Phòng ban"
                  onChange={(e) => {
                    handlePersonnelChange(e);
                  }}
                  disabled={!personnelForm.enterpriseId}
                >
                  <MenuItem value="">Không chọn phòng ban</MenuItem>
                  {personnelForm.enterpriseId ? (
                    filteredDepartmentOptions &&
                      filteredDepartmentOptions.length > 0 ? (
                      filteredDepartmentOptions.map((dept) => (
                        <MenuItem
                          key={dept.departmentId}
                          value={String(dept.departmentId)}
                        >
                          {dept.name || dept.departmentName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <em>Không có phòng ban thuộc xí nghiệp này</em>
                      </MenuItem>
                    )
                  ) : (
                    <MenuItem disabled>
                      <em>Vui lòng chọn xí nghiệp trước</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              {/* Ẩn tùy chọn trạng thái trong dialog chỉnh sửa thông tin nhân sự */}
              {personnelError.submit && (
                <Typography color="error">{personnelError.submit}</Typography>
              )}
            </Box>
          )}
        </AppTabPanel>

        <AppTabPanel value={tab} index={1}>
          {/* Tab 2: Trình độ */}
          {tab === 1 && (
            <Box>
              <Box display="flex" gap={2} mb={2} marginTop={2}>
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Trình độ</InputLabel>
                  <Select
                    name="qualificationName"
                    value={qualificationForm.qualificationName}
                    label="Trình độ"
                    onChange={handleQualificationChange}
                  >
                    {qualificationOptions.map((option) => (
                      <MenuItem
                        key={option.qualificationId}
                        value={option.qualificationName}
                      >
                        {option.qualificationName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Chuyên ngành</InputLabel>
                  <Select
                    name="major"
                    value={qualificationForm.major}
                    label="Chuyên ngành"
                    onChange={handleQualificationChange}
                  >
                    {majorOptions.map((option) => (
                      <MenuItem key={option.id} value={option.name}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Năm tốt nghiệp"
                  name="graduationYear"
                  value={qualificationForm.graduationYear}
                  onChange={handleQualificationChange}
                  type="number"
                  inputProps={{ min: 1900, max: 2100 }}
                />
                <AppButton
                  variant="contained"
                  onClick={handleAddQualification}
                  startIcon={<AddIcon />}
                >
                  Thêm
                </AppButton>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Trình độ</TableCell>
                      <TableCell>Chuyên ngành</TableCell>
                      <TableCell>Năm tốt nghiệp</TableCell>
                      <TableCell>Hành động</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {qualifications.map((q, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{q.qualificationName}</TableCell>
                        <TableCell>{q.major}</TableCell>
                        <TableCell>{q.graduationYear}</TableCell>
                        <TableCell>
                          <Tooltip title="Xóa">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteQualification(idx)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </AppTabPanel>

        <AppTabPanel value={tab} index={2}>
          {/* Tab 3: Chứng chỉ */}
          {tab === 2 && (
            <Box>
              <Box display="flex" gap={2} mb={2} marginTop={2}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Tên chứng chỉ</InputLabel>
                  <Select
                    name="certificateName"
                    value={certificateForm.certificateName}
                    label="Tên chứng chỉ"
                    onChange={handleCertificateChange}
                  >
                    {certificationOptions.map((option) => (
                      <MenuItem key={option.id} value={option.name}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Ngày cấp"
                  name="issueDate"
                  value={certificateForm.issueDate}
                  onChange={handleCertificateChange}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
                <AppButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddCertificate}
                >
                  Thêm
                </AppButton>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tên chứng chỉ</TableCell>
                      <TableCell>Ngày cấp</TableCell>
                      <TableCell>Hành động</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {certificates.map((c, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{c.certificateName}</TableCell>
                        <TableCell>{formatDate(c.issueDate)}</TableCell>
                        <TableCell>
                          <Tooltip title="Xóa">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteCertificate(idx)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </AppTabPanel>
        <AppTabPanel value={tab} index={3}>
          {/* Tab 4: Đính kèm */}
          {tab === 3 && (
            <Box>
              <Box display="flex" gap={2} mb={2} marginTop={2}>
                <FileDropzone
                  onDrop={(acceptedFiles) => {
                    if (acceptedFiles && acceptedFiles.length > 0) {
                      setDocUpload((prev) => ({
                        ...prev,
                        file: acceptedFiles[0],
                      }));
                    }
                  }}
                  file={docUpload.file}
                />
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Loại tài liệu</InputLabel>
                  <Select
                    value={docUpload.documentType}
                    label="Loại tài liệu"
                    onChange={handleDocTypeChange}
                  >
                    {documentTypeOptions.map((dt) => (
                      <MenuItem key={dt.id} value={dt.name}>
                        {dt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Mô tả"
                  value={docUpload.description}
                  onChange={handleDocDescChange}
                  sx={{ minWidth: 200 }}
                />
                <AppButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddDocument}
                  disabled={!docUpload.file || !docUpload.documentType}
                >
                  Thêm
                </AppButton>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tên file</TableCell>
                      <TableCell>Loại tài liệu</TableCell>
                      <TableCell>Mô tả</TableCell>
                      <TableCell>Hành động</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Hiển thị các tài liệu đang được thêm mới */}
                    {documents.length === 0 && savedDocuments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Chưa có file đính kèm
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {documents.map((doc, idx) => (
                          <TableRow key={`new-${idx}`}>
                            <TableCell>{doc.file?.name}</TableCell>
                            <TableCell>{doc.documentType}</TableCell>
                            <TableCell>{doc.description}</TableCell>
                            <TableCell>
                              <Tooltip title="Xem trước">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handlePreviewDocument(doc, false)
                                  }
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Xóa">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteDocument(idx)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Hiển thị các tài liệu đã được lưu trước đó */}
                        {savedDocuments && savedDocuments.length > 0
                          ? savedDocuments.map((doc, idx) => (
                            <TableRow
                              key={`saved-${doc.id ||
                                doc.documentId ||
                                doc.personnelDocumentId ||
                                idx
                                }`}
                            >
                              <TableCell>
                                {doc.documentName ||
                                  doc.fileName ||
                                  "Không có tên"}
                              </TableCell>
                              <TableCell>
                                {doc.documentType || "Không xác định"}
                              </TableCell>
                              <TableCell>{doc.description || ""}</TableCell>
                              <TableCell>
                                <Tooltip title="Xem trước">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      handlePreviewDocument(doc, true)
                                    }
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Tải tài liệu">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={async () => {
                                      // Kiểm tra xem có ID tài liệu không
                                      const docId =
                                        doc.id ||
                                        doc.documentId ||
                                        doc.personnelDocumentId;

                                      if (docId) {
                                        try {
                                          setAlertInfo({
                                            open: true,
                                            message: "Đang tải tài liệu...",
                                            severity: "info",
                                          });

                                          // Sử dụng service với axios để tải file, token sẽ tự động được đính kèm
                                          const axiosResponse =
                                            await downloadPersonnelDocument(
                                              docId,
                                            );

                                          const response = {
                                            ok: axiosResponse.status === 200,
                                            status: axiosResponse.status,
                                            statusText:
                                              axiosResponse.statusText,
                                            headers: {
                                              get: (name) => {
                                                if (
                                                  name === "content-disposition"
                                                ) {
                                                  return axiosResponse.headers[
                                                    "content-disposition"
                                                  ];
                                                }
                                                return null;
                                              },
                                            },
                                            blob: () =>
                                              Promise.resolve(
                                                axiosResponse.data,
                                              ),
                                          };

                                          if (response.ok) {
                                            // Lấy blob và tạo URL từ blob
                                            const blob = await response.blob();
                                            const url =
                                              window.URL.createObjectURL(blob);

                                            // Tìm filename từ headers
                                            const contentDisposition =
                                              response.headers.get(
                                                "content-disposition",
                                              );
                                            let filename = "document";
                                            if (
                                              contentDisposition &&
                                              contentDisposition.includes(
                                                "filename=",
                                              )
                                            ) {
                                              filename = contentDisposition
                                                .split("filename=")[1]
                                                .trim();
                                              if (
                                                filename.startsWith('"') &&
                                                filename.endsWith('"')
                                              ) {
                                                filename = filename.slice(
                                                  1,
                                                  -1,
                                                );
                                              }
                                            } else {
                                              // Sử dụng tên file từ document nếu có
                                              filename =
                                                doc.documentName ||
                                                doc.fileName ||
                                                "document";
                                            }

                                            // Tạo link tải xuống và kích hoạt nó
                                            const a =
                                              document.createElement("a");
                                            a.href = url;
                                            a.download = filename;
                                            a.target = "_blank";
                                            document.body.appendChild(a);
                                            a.click();

                                            // Dọn dẹp
                                            window.URL.revokeObjectURL(url);
                                            document.body.removeChild(a);

                                            setAlertInfo({
                                              open: true,
                                              message:
                                                "Tải tài liệu thành công",
                                              severity: "success",
                                            });
                                          } else {
                                            console.error(
                                              "HTTP Error:",
                                              response.status,
                                              response.statusText,
                                            );
                                            let errorMessage = `Lỗi ${response.status}: `;

                                            switch (response.status) {
                                              case 404:
                                                errorMessage +=
                                                  "Không tìm thấy tài liệu. URL gọi API có thể không đúng.";
                                                break;
                                              case 401:
                                                errorMessage +=
                                                  "Bạn cần đăng nhập lại để xem tài liệu";
                                                break;
                                              case 403:
                                                errorMessage +=
                                                  "Bạn không có quyền xem tài liệu này";
                                                break;
                                              default:
                                                errorMessage +=
                                                  "Không thể tải tài liệu";
                                            }

                                            try {
                                              // Thử đọc thông báo lỗi chi tiết từ response
                                              const errorData =
                                                await response.text();
                                              console.error(
                                                "Error response body:",
                                                errorData,
                                              );
                                            } catch (e) {
                                              // Nếu không đọc được text, bỏ qua
                                              console.error(
                                                "Could not read error response text:",
                                                e,
                                              );
                                            }

                                            throw new Error(errorMessage);
                                          }
                                        } catch (error) {
                                          console.error(
                                            "Error downloading document:",
                                            error,
                                          );

                                          let errorMessage =
                                            error.message ||
                                            "Không thể tải tài liệu.";

                                          // Kiểm tra lỗi từ axios
                                          if (error.response) {
                                            // Server trả về status code nằm ngoài 2xx
                                            console.error(
                                              "Error response:",
                                              error.response,
                                            );
                                            errorMessage = `Lỗi ${error.response.status}: `;

                                            switch (error.response.status) {
                                              case 404:
                                                errorMessage +=
                                                  "Không tìm thấy tài liệu. URL API không đúng.";
                                                break;
                                              case 401:
                                                errorMessage +=
                                                  "Phiên làm việc đã hết hạn. Vui lòng đăng nhập lại.";
                                                break;
                                              case 403:
                                                errorMessage +=
                                                  "Bạn không có quyền xem tài liệu này.";
                                                break;
                                              default:
                                                errorMessage +=
                                                  "Không thể tải tài liệu.";
                                            }
                                          } else if (error.request) {
                                            // Request được gửi nhưng không nhận được response
                                            console.error(
                                              "Error request:",
                                              error.request,
                                            );
                                            errorMessage =
                                              "Không nhận được phản hồi từ máy chủ. Vui lòng thử lại sau.";
                                          }

                                          setAlertInfo({
                                            open: true,
                                            message: errorMessage,
                                            severity: "error",
                                          });
                                        }
                                      } else {
                                        console.error(
                                          "Document ID not found:",
                                          doc,
                                        );
                                        setAlertInfo({
                                          open: true,
                                          message: "Không thể tải tài liệu này",
                                          severity: "error",
                                        });
                                      }
                                    }}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Xóa">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={async () => {
                                      try {
                                        const docId =
                                          doc.id ||
                                          doc.documentId ||
                                          doc.personnelDocumentId;
                                        if (docId) {
                                          await deletePersonnelDocument(docId);
                                          setSavedDocuments(
                                            savedDocuments.filter((d) => {
                                              const currentId =
                                                d.id ||
                                                d.documentId ||
                                                d.personnelDocumentId;
                                              return currentId !== docId;
                                            }),
                                          );
                                        } else {
                                          console.error(
                                            "Cannot delete document without ID:",
                                            doc,
                                          );
                                        }
                                      } catch (error) {
                                        console.error(
                                          "Error deleting document:",
                                          error,
                                        );
                                      }
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                          : null}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </AppTabPanel>
      </DialogContent>

      <DialogActions>
        <AppButton variant="outlined" onClick={handleClose}>
          Hủy
        </AppButton>
        <AppButton
          variant="contained"
          onClick={handleSavePersonnel}
          disabled={
            submitting ||
            !personnelForm.fullName ||
            !personnelForm.title ||
            Object.keys(personnelError).length > 0
          }
        >
          {isEdit ? "Cập nhật" : "Lưu nhân sự"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

// Component để hiển thị nội dung text file
const TextPreviewContent = ({ fileBlob }) => {
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!fileBlob) {
      setContent("Không có nội dung");
      setIsLoading(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        setContent(event.target.result);
      } catch (error) {
        console.error("Error reading file:", error);
        setContent("Lỗi khi đọc file");
      } finally {
        setIsLoading(false);
      }
    };

    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      setContent("Lỗi khi đọc file");
      setIsLoading(false);
    };

    reader.readAsText(fileBlob);
  }, [fileBlob]);

  return <>{isLoading ? "Đang tải..." : content}</>;
};

export default AddPersonnelModal;

// {/* Tab 1: Thông tin nhân sự */}
//         {tab === 0 && (
//           <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
//             <TextField
//               label="Họ và tên"
//               name="fullName"
//               value={personnelForm.fullName}
//               onChange={handlePersonnelChange}
//               required
//               error={!!personnelError.fullName}
//               helperText={personnelError.fullName}
//             />
//             <FormControl fullWidth error={!!personnelError.title}>
//               <InputLabel>Chức vụ</InputLabel>
//               <Select
//                 name="title"
//                 value={personnelForm.title}
//                 label="Chức vụ"
//                 onChange={handlePersonnelChange}
//               >
//                 {titleOptions.map((option) => (
//                   <MenuItem key={option.id} value={option.name}>
//                     {option.name}
//                   </MenuItem>
//                 ))}
//               </Select>
//               {personnelError.title && (
//                 <Typography color="error" variant="caption" sx={{ ml: 2 }}>
//                   {personnelError.title}
//                 </Typography>
//               )}
//             </FormControl>
//             <TextField
//               label="Email"
//               name="email"
//               value={personnelForm.email}
//               onChange={handlePersonnelChange}
//               error={!!personnelError.email}
//               helperText={personnelError.email}
//             />
//             <TextField
//               label="Số điện thoại"
//               name="phoneNumber"
//               value={personnelForm.phoneNumber}
//               onChange={handlePersonnelChange}
//             />
//             <Box display="flex" gap={1} alignItems="flex-start">
//               <TextField
//                 label="Thứ tự hiển thị"
//                 name="displayOrder"
//                 type="number"
//                 value={personnelForm.displayOrder}
//                 onChange={handlePersonnelChange}
//                 inputProps={{ min: 0 }}
//                 helperText="Thứ tự sắp xếp trong danh sách (0 = đầu tiên)"
//                 sx={{ flex: 1 }}
//               />
//               <Tooltip title="Tự động lấy số thứ tự tiếp theo">
//                 <AppButton
//                   variant="outlined"
//                   // size="medium"
//                   onClick={handleAutoDisplayOrder}
//                   startIcon={<AutoFixHighIcon />}
//                   sx={{
//                     // minWidth: "auto",
//                     // px: 1,
//                     // textTransform: "none",
//                     height: "56px",
//                     // mt: 0,
//                   }}
//                 >
//                   Auto
//                 </AppButton>
//               </Tooltip>
//             </Box>
//             <FormControl fullWidth>
//               <InputLabel>Xí nghiệp</InputLabel>
//               <Select
//                 name="enterpriseId"
//                 value={personnelForm.enterpriseId || ""}
//                 label="Xí nghiệp"
//                 onChange={(e) => {
//                   handlePersonnelChange(e);
//                 }}
//               >
//                 <MenuItem value="">Không chọn xí nghiệp</MenuItem>
//                 {enterpriseOptions && enterpriseOptions.length > 0 ? (
//                   enterpriseOptions.map((enterprise) => (
//                     <MenuItem
//                       key={enterprise.enterpriseId}
//                       value={String(enterprise.enterpriseId)}
//                     >
//                       {enterprise.name}
//                     </MenuItem>
//                   ))
//                 ) : (
//                   <MenuItem disabled>
//                     <em>Đang tải dữ liệu...</em>
//                   </MenuItem>
//                 )}
//               </Select>
//             </FormControl>
//             <FormControl fullWidth>
//               <InputLabel>Phòng ban</InputLabel>
//               <Select
//                 name="departmentId"
//                 value={personnelForm.departmentId || ""}
//                 label="Phòng ban"
//                 onChange={(e) => {
//                   handlePersonnelChange(e);
//                 }}
//                 disabled={!personnelForm.enterpriseId}
//               >
//                 <MenuItem value="">Không chọn phòng ban</MenuItem>
//                 {personnelForm.enterpriseId ? (
//                   filteredDepartmentOptions &&
//                     filteredDepartmentOptions.length > 0 ? (
//                     filteredDepartmentOptions.map((dept) => (
//                       <MenuItem
//                         key={dept.departmentId}
//                         value={String(dept.departmentId)}
//                       >
//                         {dept.name || dept.departmentName}
//                       </MenuItem>
//                     ))
//                   ) : (
//                     <MenuItem disabled>
//                       <em>Không có phòng ban thuộc xí nghiệp này</em>
//                     </MenuItem>
//                   )
//                 ) : (
//                   <MenuItem disabled>
//                     <em>Vui lòng chọn xí nghiệp trước</em>
//                   </MenuItem>
//                 )}
//               </Select>
//             </FormControl>
//             {/* Ẩn tùy chọn trạng thái trong dialog chỉnh sửa thông tin nhân sự */}
//             {personnelError.submit && (
//               <Typography color="error">{personnelError.submit}</Typography>
//             )}
//           </Box>
//         )}
//         {/* Tab 2: Trình độ */}
//         {tab === 1 && (
//           <Box>
//             <Box display="flex" gap={2} mb={2}>
//               <FormControl sx={{ minWidth: 160 }}>
//                 <InputLabel>Trình độ</InputLabel>
//                 <Select
//                   name="qualificationName"
//                   value={qualificationForm.qualificationName}
//                   label="Trình độ"
//                   onChange={handleQualificationChange}
//                 >
//                   {qualificationOptions.map((option) => (
//                     <MenuItem
//                       key={option.qualificationId}
//                       value={option.qualificationName}
//                     >
//                       {option.qualificationName}
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//               <FormControl sx={{ minWidth: 160 }}>
//                 <InputLabel>Chuyên ngành</InputLabel>
//                 <Select
//                   name="major"
//                   value={qualificationForm.major}
//                   label="Chuyên ngành"
//                   onChange={handleQualificationChange}
//                 >
//                   {majorOptions.map((option) => (
//                     <MenuItem key={option.id} value={option.name}>
//                       {option.name}
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//               <TextField
//                 label="Năm tốt nghiệp"
//                 name="graduationYear"
//                 value={qualificationForm.graduationYear}
//                 onChange={handleQualificationChange}
//                 type="number"
//                 inputProps={{ min: 1900, max: 2100 }}
//               />
//               <Button
//                 variant="contained"
//                 onClick={handleAddQualification}
//                 startIcon={<AddIcon />}
//               >
//                 Thêm
//               </Button>
//             </Box>
//             <TableContainer component={Paper} variant="outlined">
//               <Table size="small">
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>Trình độ</TableCell>
//                     <TableCell>Chuyên ngành</TableCell>
//                     <TableCell>Năm tốt nghiệp</TableCell>
//                     <TableCell>Action</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {qualifications.map((q, idx) => (
//                     <TableRow key={idx}>
//                       <TableCell>{q.qualificationName}</TableCell>
//                       <TableCell>{q.major}</TableCell>
//                       <TableCell>{q.graduationYear}</TableCell>
//                       <TableCell>
//                         <Tooltip title="Xóa">
//                           <IconButton
//                             size="small"
//                             onClick={() => handleDeleteQualification(idx)}
//                           >
//                             <DeleteIcon />
//                           </IconButton>
//                         </Tooltip>
//                       </TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//           </Box>
//         )}
//         {/* Tab 3: Chứng chỉ */}
//         {tab === 2 && (
//           <Box>
//             <Box display="flex" gap={2} mb={2}>
//               <FormControl sx={{ minWidth: 200 }}>
//                 <InputLabel>Tên chứng chỉ</InputLabel>
//                 <Select
//                   name="certificateName"
//                   value={certificateForm.certificateName}
//                   label="Tên chứng chỉ"
//                   onChange={handleCertificateChange}
//                 >
//                   {certificationOptions.map((option) => (
//                     <MenuItem key={option.id} value={option.name}>
//                       {option.name}
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//               <TextField
//                 label="Ngày cấp"
//                 name="issueDate"
//                 value={certificateForm.issueDate}
//                 onChange={handleCertificateChange}
//                 type="date"
//                 InputLabelProps={{ shrink: true }}
//               />
//               <Button
//                 variant="contained"
//                 onClick={handleAddCertificate}
//                 startIcon={<AddIcon />}
//               >
//                 Thêm
//               </Button>
//             </Box>
//             <TableContainer component={Paper} variant="outlined">
//               <Table size="small">
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>Tên chứng chỉ</TableCell>
//                     <TableCell>Ngày cấp</TableCell>
//                     <TableCell>Action</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {certificates.map((c, idx) => (
//                     <TableRow key={idx}>
//                       <TableCell>{c.certificateName}</TableCell>
//                       <TableCell>{formatDate(c.issueDate)}</TableCell>
//                       <TableCell>
//                         <Tooltip title="Xóa">
//                           <IconButton
//                             size="small"
//                             onClick={() => handleDeleteCertificate(idx)}
//                           >
//                             <DeleteIcon />
//                           </IconButton>
//                         </Tooltip>
//                       </TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//           </Box>
//         )}
//         {/* Tab 4: Đính kèm */}
//         {tab === 3 && (
//           <Box>
//             <Box display="flex" gap={2} mb={2}>
//               <FileDropzone
//                 onDrop={(acceptedFiles) => {
//                   if (acceptedFiles && acceptedFiles.length > 0) {
//                     setDocUpload((prev) => ({
//                       ...prev,
//                       file: acceptedFiles[0],
//                     }));
//                   }
//                 }}
//                 file={docUpload.file}
//               />
//               <FormControl sx={{ minWidth: 160 }}>
//                 <InputLabel>Loại tài liệu</InputLabel>
//                 <Select
//                   value={docUpload.documentType}
//                   label="Loại tài liệu"
//                   onChange={handleDocTypeChange}
//                 >
//                   {documentTypeOptions.map((dt) => (
//                     <MenuItem key={dt.id} value={dt.name}>
//                       {dt.name}
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//               <TextField
//                 label="Mô tả"
//                 value={docUpload.description}
//                 onChange={handleDocDescChange}
//                 sx={{ minWidth: 200 }}
//               />
//               <Button
//                 variant="contained"
//                 onClick={handleAddDocument}
//                 disabled={!docUpload.file || !docUpload.documentType}
//               >
//                 Thêm
//               </Button>
//             </Box>
//             <TableContainer component={Paper} variant="outlined">
//               <Table size="small">
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>Tên file</TableCell>
//                     <TableCell>Loại tài liệu</TableCell>
//                     <TableCell>Mô tả</TableCell>
//                     <TableCell>Hành động</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {/* Hiển thị các tài liệu đang được thêm mới */}
//                   {documents.length === 0 && savedDocuments.length === 0 ? (
//                     <TableRow>
//                       <TableCell colSpan={4} align="center">
//                         Chưa có file đính kèm
//                       </TableCell>
//                     </TableRow>
//                   ) : (
//                     <>
//                       {documents.map((doc, idx) => (
//                         <TableRow key={`new-${idx}`}>
//                           <TableCell>{doc.file?.name}</TableCell>
//                           <TableCell>{doc.documentType}</TableCell>
//                           <TableCell>{doc.description}</TableCell>
//                           <TableCell>
//                             <Tooltip title="Xem trước">
//                               <IconButton
//                                 size="small"
//                                 color="primary"
//                                 onClick={() =>
//                                   handlePreviewDocument(doc, false)
//                                 }
//                               >
//                                 <VisibilityIcon />
//                               </IconButton>
//                             </Tooltip>
//                             <Tooltip title="Xóa">
//                               <IconButton
//                                 size="small"
//                                 color="error"
//                                 onClick={() => handleDeleteDocument(idx)}
//                               >
//                                 <DeleteIcon />
//                               </IconButton>
//                             </Tooltip>
//                           </TableCell>
//                         </TableRow>
//                       ))}
//                       {/* Hiển thị các tài liệu đã được lưu trước đó */}
//                       {savedDocuments && savedDocuments.length > 0
//                         ? savedDocuments.map((doc, idx) => (
//                           <TableRow
//                             key={`saved-${doc.id ||
//                               doc.documentId ||
//                               doc.personnelDocumentId ||
//                               idx
//                               }`}
//                           >
//                             <TableCell>
//                               {doc.documentName ||
//                                 doc.fileName ||
//                                 "Không có tên"}
//                             </TableCell>
//                             <TableCell>
//                               {doc.documentType || "Không xác định"}
//                             </TableCell>
//                             <TableCell>{doc.description || ""}</TableCell>
//                             <TableCell>
//                               <Tooltip title="Xem trước">
//                                 <IconButton
//                                   size="small"
//                                   color="primary"
//                                   onClick={() =>
//                                     handlePreviewDocument(doc, true)
//                                   }
//                                 >
//                                   <VisibilityIcon />
//                                 </IconButton>
//                               </Tooltip>
//                               <Tooltip title="Tải tài liệu">
//                                 <IconButton
//                                   size="small"
//                                   color="primary"
//                                   onClick={async () => {
//                                     // Kiểm tra xem có ID tài liệu không
//                                     const docId =
//                                       doc.id ||
//                                       doc.documentId ||
//                                       doc.personnelDocumentId;

//                                     if (docId) {
//                                       try {
//                                         setAlertInfo({
//                                           open: true,
//                                           message: "Đang tải tài liệu...",
//                                           severity: "info",
//                                         });

//                                         // Sử dụng service với axios để tải file, token sẽ tự động được đính kèm
//                                         const axiosResponse =
//                                           await downloadPersonnelDocument(
//                                             docId,
//                                           );

//                                         const response = {
//                                           ok: axiosResponse.status === 200,
//                                           status: axiosResponse.status,
//                                           statusText:
//                                             axiosResponse.statusText,
//                                           headers: {
//                                             get: (name) => {
//                                               if (
//                                                 name === "content-disposition"
//                                               ) {
//                                                 return axiosResponse.headers[
//                                                   "content-disposition"
//                                                 ];
//                                               }
//                                               return null;
//                                             },
//                                           },
//                                           blob: () =>
//                                             Promise.resolve(
//                                               axiosResponse.data,
//                                             ),
//                                         };

//                                         if (response.ok) {
//                                           // Lấy blob và tạo URL từ blob
//                                           const blob = await response.blob();
//                                           const url =
//                                             window.URL.createObjectURL(blob);

//                                           // Tìm filename từ headers
//                                           const contentDisposition =
//                                             response.headers.get(
//                                               "content-disposition",
//                                             );
//                                           let filename = "document";
//                                           if (
//                                             contentDisposition &&
//                                             contentDisposition.includes(
//                                               "filename=",
//                                             )
//                                           ) {
//                                             filename = contentDisposition
//                                               .split("filename=")[1]
//                                               .trim();
//                                             if (
//                                               filename.startsWith('"') &&
//                                               filename.endsWith('"')
//                                             ) {
//                                               filename = filename.slice(
//                                                 1,
//                                                 -1,
//                                               );
//                                             }
//                                           } else {
//                                             // Sử dụng tên file từ document nếu có
//                                             filename =
//                                               doc.documentName ||
//                                               doc.fileName ||
//                                               "document";
//                                           }

//                                           // Tạo link tải xuống và kích hoạt nó
//                                           const a =
//                                             document.createElement("a");
//                                           a.href = url;
//                                           a.download = filename;
//                                           a.target = "_blank";
//                                           document.body.appendChild(a);
//                                           a.click();

//                                           // Dọn dẹp
//                                           window.URL.revokeObjectURL(url);
//                                           document.body.removeChild(a);

//                                           setAlertInfo({
//                                             open: true,
//                                             message:
//                                               "Tải tài liệu thành công",
//                                             severity: "success",
//                                           });
//                                         } else {
//                                           console.error(
//                                             "HTTP Error:",
//                                             response.status,
//                                             response.statusText,
//                                           );
//                                           let errorMessage = `Lỗi ${response.status}: `;

//                                           switch (response.status) {
//                                             case 404:
//                                               errorMessage +=
//                                                 "Không tìm thấy tài liệu. URL gọi API có thể không đúng.";
//                                               break;
//                                             case 401:
//                                               errorMessage +=
//                                                 "Bạn cần đăng nhập lại để xem tài liệu";
//                                               break;
//                                             case 403:
//                                               errorMessage +=
//                                                 "Bạn không có quyền xem tài liệu này";
//                                               break;
//                                             default:
//                                               errorMessage +=
//                                                 "Không thể tải tài liệu";
//                                           }

//                                           try {
//                                             // Thử đọc thông báo lỗi chi tiết từ response
//                                             const errorData =
//                                               await response.text();
//                                             console.error(
//                                               "Error response body:",
//                                               errorData,
//                                             );
//                                           } catch (e) {
//                                             // Nếu không đọc được text, bỏ qua
//                                             console.error(
//                                               "Could not read error response text:",
//                                               e,
//                                             );
//                                           }

//                                           throw new Error(errorMessage);
//                                         }
//                                       } catch (error) {
//                                         console.error(
//                                           "Error downloading document:",
//                                           error,
//                                         );

//                                         let errorMessage =
//                                           error.message ||
//                                           "Không thể tải tài liệu.";

//                                         // Kiểm tra lỗi từ axios
//                                         if (error.response) {
//                                           // Server trả về status code nằm ngoài 2xx
//                                           console.error(
//                                             "Error response:",
//                                             error.response,
//                                           );
//                                           errorMessage = `Lỗi ${error.response.status}: `;

//                                           switch (error.response.status) {
//                                             case 404:
//                                               errorMessage +=
//                                                 "Không tìm thấy tài liệu. URL API không đúng.";
//                                               break;
//                                             case 401:
//                                               errorMessage +=
//                                                 "Phiên làm việc đã hết hạn. Vui lòng đăng nhập lại.";
//                                               break;
//                                             case 403:
//                                               errorMessage +=
//                                                 "Bạn không có quyền xem tài liệu này.";
//                                               break;
//                                             default:
//                                               errorMessage +=
//                                                 "Không thể tải tài liệu.";
//                                           }
//                                         } else if (error.request) {
//                                           // Request được gửi nhưng không nhận được response
//                                           console.error(
//                                             "Error request:",
//                                             error.request,
//                                           );
//                                           errorMessage =
//                                             "Không nhận được phản hồi từ máy chủ. Vui lòng thử lại sau.";
//                                         }

//                                         setAlertInfo({
//                                           open: true,
//                                           message: errorMessage,
//                                           severity: "error",
//                                         });
//                                       }
//                                     } else {
//                                       console.error(
//                                         "Document ID not found:",
//                                         doc,
//                                       );
//                                       setAlertInfo({
//                                         open: true,
//                                         message: "Không thể tải tài liệu này",
//                                         severity: "error",
//                                       });
//                                     }
//                                   }}
//                                 >
//                                   <DownloadIcon />
//                                 </IconButton>
//                               </Tooltip>
//                               <Tooltip title="Xóa">
//                                 <IconButton
//                                   size="small"
//                                   color="error"
//                                   onClick={async () => {
//                                     try {
//                                       const docId =
//                                         doc.id ||
//                                         doc.documentId ||
//                                         doc.personnelDocumentId;
//                                       if (docId) {
//                                         await deletePersonnelDocument(docId);
//                                         setSavedDocuments(
//                                           savedDocuments.filter((d) => {
//                                             const currentId =
//                                               d.id ||
//                                               d.documentId ||
//                                               d.personnelDocumentId;
//                                             return currentId !== docId;
//                                           }),
//                                         );
//                                       } else {
//                                         console.error(
//                                           "Cannot delete document without ID:",
//                                           doc,
//                                         );
//                                       }
//                                     } catch (error) {
//                                       console.error(
//                                         "Error deleting document:",
//                                         error,
//                                       );
//                                     }
//                                   }}
//                                 >
//                                   <DeleteIcon />
//                                 </IconButton>
//                               </Tooltip>
//                             </TableCell>
//                           </TableRow>
//                         ))
//                         : null}
//                     </>
//                   )}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//           </Box>
//         )}